package link.erpango.quip.utils

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.DateTimeComponents
import kotlinx.datetime.format.DateTimeComponents.Companion.Format
import kotlinx.datetime.format.DateTimeFormat
import kotlinx.datetime.format.Padding
import kotlinx.datetime.format.char
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import link.erpango.quip.QuipConstants
import kotlin.time.Clock
import kotlin.time.DurationUnit
import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import kotlin.time.toDuration

@OptIn(ExperimentalTime::class)
object DatetimeUtils {

    /**
     * Devuelve un objeto [DateTimeFormat] configurado para formatear [Instant]s
     * basado en el String de formato proporcionado.
     *
     * @param formatString La cadena que representa el formato deseado (por ejemplo, QuipConstants.DateFormat.utcPattern1).
     * @return Un [DateTimeFormat] configurado para el formato dado.
     * @throws IllegalArgumentException Si el formato proporcionado no es reconocido.
     */
    fun getInstantFormat(formatString: String): DateTimeFormat<DateTimeComponents> {
        return when (formatString) {
            QuipConstants.DateFormat.utcPattern1, QuipConstants.DateFormat.backendUtcPattern1 -> Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                char('Z')
            }
            QuipConstants.DateFormat.utcPattern2 -> Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                // Especifica 3 dígitos para los milisegundos (SSS)
                secondFraction(3)
                char('Z')
            }
            QuipConstants.DateFormat.utcPattern3 -> Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                // Especifica 6 dígitos para los microsegundos (SSSSSS)
                secondFraction(6)
                char('Z')
            }
            QuipConstants.DateFormat.utcPattern4 -> Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                // 6 dígitos para los microsegundos
                secondFraction(6)
                // Offset de zona horaria en formato +HH:mm o -HH:mm
                char('+')
                offsetHours()
                char(':')
                offsetMinutesOfHour()
            }
            QuipConstants.DateFormat.noUtcPattern1 -> Format {
                day()
                char('/')
                monthNumber()
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            QuipConstants.DateFormat.noUtcPattern2 -> Format {
                day()
                char('/')
                monthNumber()
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
            }
            QuipConstants.DateFormat.noUtcPattern3 -> Format {
                // Día sin cero delante
                day(padding = Padding.NONE)
                char('/')
                // Mes sin 0 delante
                monthNumber(padding = Padding.NONE)
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            QuipConstants.DateFormat.noUtcPattern4 -> Format {
                day(padding = Padding.NONE)
                char('/')
                monthNumber(padding = Padding.NONE)
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
            }
            QuipConstants.DateFormat.noUtcPatternEnglishWithoutTime -> Format {
                year()
                char('/')
                monthNumber()
                char('/')
                day()
            }
            QuipConstants.DateFormat.noUtcPatternSpanishWithoutTime -> Format {
                day()
                char('/')
                monthNumber()
                char('/')
                year()
            }
            QuipConstants.DateFormat.noUtcPatternSpanishWithoutTimeWithDash -> Format {
                day()
                char('-')
                monthNumber()
                char('-')
                year()
            }
            QuipConstants.DateFormat.dateInsideLog -> Format {
                day()
                char(' ')
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            QuipConstants.DateFormat.timePattern -> Format {
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            else -> throw IllegalArgumentException("Formato de fecha no reconocido: $formatString")
        }
    }


    /**
     * Parsea los mismos formatos que getInstantFormat (excepto QuipConstants.DateFormat.utcPattern4 , pero para LocalDateTime.
     * */
    fun getLocalDateTimeFormat(format: String): DateTimeFormat<LocalDateTime> {
        return when (format) {
            QuipConstants.DateFormat.utcPattern1, QuipConstants.DateFormat.backendUtcPattern1 -> LocalDateTime.Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                char('Z')
            }
            QuipConstants.DateFormat.utcPattern2 -> LocalDateTime.Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                secondFraction(3)
                char('Z')
            }
            QuipConstants.DateFormat.utcPattern3 -> LocalDateTime.Format {
                year()
                char('-')
                monthNumber()
                char('-')
                day()
                char('T')
                hour()
                char(':')
                minute()
                char(':')
                second()
                secondFraction(6)
                char('Z')
            }
            QuipConstants.DateFormat.noUtcPattern1 -> LocalDateTime.Format {
                day()
                char('/')
                monthNumber()
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            QuipConstants.DateFormat.noUtcPattern2 -> LocalDateTime.Format {
                day()
                char('/')
                monthNumber()
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
            }
            QuipConstants.DateFormat.noUtcPattern3 -> LocalDateTime.Format {
                // Día sin 0 delante
                day(padding = Padding.NONE)
                char('/')
                // Mes sin 0 delante
                monthNumber(padding = Padding.NONE)
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            QuipConstants.DateFormat.noUtcPattern4 -> LocalDateTime.Format {
                day(padding = Padding.NONE)
                char('/')
                monthNumber(padding = Padding.NONE)
                char('/')
                year()
                char(' ')
                hour()
                char(':')
                minute()
            }
            QuipConstants.DateFormat.noUtcPatternEnglishWithoutTime -> LocalDateTime.Format {
                year()
                char('/')
                monthNumber()
                char('/')
                day()
            }
            QuipConstants.DateFormat.noUtcPatternSpanishWithoutTime -> LocalDateTime.Format {
                day()
                char('/')
                monthNumber()
                char('/')
                year()
            }
            QuipConstants.DateFormat.noUtcPatternSpanishWithoutTimeWithDash -> LocalDateTime.Format {
                day()
                char('-')
                monthNumber()
                char('-')
                year()
            }
            QuipConstants.DateFormat.dateInsideLog -> LocalDateTime.Format {
                day()
                char(' ')
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            QuipConstants.DateFormat.timePattern -> LocalDateTime.Format {
                hour()
                char(':')
                minute()
                char(':')
                second()
            }
            else -> throw IllegalArgumentException("Formato de fecha no reconocido: $format")
        }
    }



    // Devuelve la fecha actual en formato utcPattern1 (yyyy-MM-dd'T'HH:mm:ss'Z') y en zona horaria +00
    fun current(): String {
        return Clock.System.now().format(
            format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
            offset = TimeZone.UTC.offset//offsetAt(Clock.System.now()) // Esto obtiene la zona horaria del dispositivo
        )
    }

    fun currentDateTimeSpanish():  String {
        return Clock.System.now().format(
            format = getInstantFormat(QuipConstants.DateFormat.noUtcPattern2),
            offset = TimeZone.UTC.offset
        )
    }

    fun current(plusDay: Int, resetMinutesAndSecond: Boolean=false): String {
        val now = Clock.System.now().plus(
            plusDay.toDuration(DurationUnit.DAYS)
        )
        val nowLocalDateTime = now.toLocalDateTime(TimeZone.UTC)
        return if (resetMinutesAndSecond) {
            LocalDateTime(
                year = nowLocalDateTime.year,
                month = nowLocalDateTime.month,
                day = nowLocalDateTime.day,
                hour = nowLocalDateTime.hour,
                minute = 0,
                second = 0,
                nanosecond = 0
            ).toInstant(TimeZone.UTC).format(
                format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
                offset = TimeZone.UTC.offset
            )
        } else {
            now.format(
                format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
                offset = TimeZone.UTC.offset
            )
        }
    }

    fun currentTimeSeconds(): Long {
        return Clock.System.now().toEpochMilliseconds() / 1000
    }

    /**
     * Devuelve el tiempo actual en milisegundos desde el epoch de Unix.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun currentTimeMillis(): Long {
        return Clock.System.now().toEpochMilliseconds()
    }

    /**
     * Devuelve la hora actual en formato UTC como cadena formateada.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun getCurrentTimeUTC(): String {
        return Clock.System.now().format(
            format = getInstantFormat(QuipConstants.DateFormat.simpleUtcPattern),
            offset = TimeZone.UTC.offset
        )
    }

    /**
     * Formatea una fecha con los componentes proporcionados.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun formatDate(year: Int, month: Int, day: Int): String {
        return "$day/${month + 1}/$year"
    }

    /**
     * Formatea una hora con los componentes proporcionados.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun formatTime(hour: Int, minute: Int): String {
        return "$hour:$minute"
    }

    /**
     * Formatea una fecha y hora con los componentes proporcionados.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun formatDateTime(year: Int, month: Int, day: Int, hour: Int, minute: Int): String {
        return "${formatDate(year, month, day)} ${formatTime(hour, minute)}"
    }

    /**
     * Formatea una fecha y hora a partir de milisegundos desde epoch.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun formatDateTime(timeInMillis: Long): String {
        val instant = Instant.fromEpochMilliseconds(timeInMillis)
        val localDateTime = instant.toLocalDateTime(TimeZone.UTC)

        return formatDateTime(
            year = localDateTime.year,
            month = localDateTime.monthNumber - 1, // Se resta 1 para que sea compatible con la implementación de DatetimeUtils
            day = localDateTime.dayOfMonth,
            hour = localDateTime.hour,
            minute = localDateTime.minute
        )
    }

    /**
     * Convierte una cadena de fecha formateada a milisegundos desde epoch.
     * Equivalente a la implementación actual de DatetimeUtils.
     */
    fun timeInMillisFromFormattedDateTime(formattedDateTime: String): Long {
        try {
            val dateTimeParts = formattedDateTime.split(" ")
            val dateParts = dateTimeParts[0].split("/")
            val timeParts = dateTimeParts[1].split(":")

            val year = dateParts[2].toInt()
            val month = dateParts[1].toInt() - 1 // El formato de entrada es 1-12, pero se guarda como 0-11
            val day = dateParts[0].toInt()
            val hour = timeParts[0].toInt()
            val minute = timeParts[1].toInt()

            // Crear LocalDateTime y convertir a Instant
            val localDateTime = kotlinx.datetime.LocalDateTime(
                year = year,
                month = kotlinx.datetime.Month(month + 1), // Se suma 1 porque kotlinx.datetime usa 1-12
                dayOfMonth = day,
                hour = hour,
                minute = minute,
                second = 0,
                nanosecond = 0
            )

            // Convertir a instante usando TimeZone.systemDefault()
            return localDateTime.toInstant(TimeZone.UTC).toEpochMilliseconds()
        } catch (e: Exception) {
            println("Error parsing formatted date time: $formattedDateTime")
            return 0L
        }
    }

    fun currentTimeMillisPlusDay(plusDay: Int): Long {
        return Clock.System.now().plus(
            plusDay.toDuration(DurationUnit.DAYS)
        ).toEpochMilliseconds()
    }

    fun datePlusSeconds(givenDate: String?, plusSeconds: Long?): String {
        if (givenDate.isNullOrEmpty() || plusSeconds == null) {
            return ""
        }
        return try {
            val dateInstant = try {
                Instant.parse(givenDate)
            } catch (e: Exception) {
                sanitizeAndParseDate(givenDate)
            }

            dateInstant?.plus(plusSeconds, DateTimeUnit.SECOND)?.format(
                format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
                offset = TimeZone.UTC.offset
            ) ?: ""
        } catch (e: Exception) {
            ""
        }
    }

    fun isAfter(date1Str: String, date2Str: String): Boolean {
        return try {
            val date1Instant = try {
                Instant.parse(date1Str)
            } catch (e: Exception) {
                sanitizeAndParseDate(date1Str) ?: return false
            }

            val date2Instant = try {
                Instant.parse(date2Str)
            } catch (e: Exception) {
                sanitizeAndParseDate(date2Str) ?: return false
            }

            date1Instant > date2Instant
        } catch (e: Exception) {
            false
        }
    }

    fun isBefore(date1Str: String, date2Str: String): Boolean {
        return isAfter(
            date1Str = date2Str,
            date2Str = date1Str
        )
    }

    fun setHoursOfDay(dateStr: String, hours: Int): String {
        return try {
            val initialDate = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return ""
            }

            val localDateTime = initialDate.toLocalDateTime(TimeZone.UTC)
            val updatedDate = LocalDateTime(
                year = localDateTime.year,
                month = localDateTime.month,
                day = localDateTime.day,
                hour = hours,
                minute = localDateTime.minute,
                second = localDateTime.second,
                nanosecond = localDateTime.nanosecond
            ).toInstant(TimeZone.UTC).format(
                format = getInstantFormat(QuipConstants.DateFormat.noUtcPattern1),
                offset = TimeZone.UTC.offset
            )
            updatedDate
        } catch (e: Exception) {
            ""
        }
    }

    // El único punto desde el que se llama a este método ya trata la posible excepción de que no pueda parsearse la fecha
    fun getDateHours(dateStr: String): Int {
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr)
            }

            if (dateInstant != null) {
                val localDateTime = dateInstant.toLocalDateTime(TimeZone.UTC)
                localDateTime.hour
            } else {
                throw IllegalArgumentException("Fecha no válida: $dateStr")
            }
        } catch (e: Exception) {
            throw IllegalArgumentException("Fecha no válida: $dateStr")
        }
    }

    fun getDiffHours(dateStr1: String, dateStr2: String): Int {
        return try {
            val date1Instant = try {
                Instant.parse(dateStr1)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr1) ?: return 0
            }

            val date2Instant = try {
                Instant.parse(dateStr2)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr2) ?: return 0
            }

            val diffMillis = date1Instant.toEpochMilliseconds() - date2Instant.toEpochMilliseconds()
            (diffMillis / 3600000).toInt() // Convertir milisegundos a horas
        } catch (e: Exception) {
            0
        }
    }

    /**
     * Si la fecha actual menos la fecha dada da una diferencia mayor que el límite en milisegundos, devuelve true
     * */
    fun isDiffMillisencodsMoreThanLimit(dateStr: String, limitMillisecond: Long): Boolean {
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr)
            }

            if (dateInstant != null) {
                val currentInstant = Clock.System.now()
                val diffMillis = currentInstant.toEpochMilliseconds() - dateInstant.toEpochMilliseconds()
                diffMillis > limitMillisecond
            } else {
                false
            }
        } catch (e: Exception) {
            false
        }
    }

    fun compareDates(date1: String?, date2: String?): Int {
        if (date1 == null && date2 == null) return 0
        if (date1 == null) return -1
        if (date2 == null) return 1
        if (date1.isEmpty() && date2.isEmpty()) return 0
        if (date1.isEmpty()) return -1
        if (date2.isEmpty()) return 1
        if (DatetimeUtils.isAfter(date1, date2)) return 1
        else if (DatetimeUtils.isBefore(date1, date2)) return -1
        else return 0
    }

    /**
     * Devuelve la fecha dada en formato español (dd/MM/yyyy HH:mm), zona horaria UTC +00
     * */
    fun formatDateSpanish(dateStr: String?): String {
        return if (dateStr.isNullOrEmpty()) {
            ""
        } else {
            this.dateToSpecificFormat(dateStr, QuipConstants.DateFormat.noUtcPattern2)
        }
    }

    fun diffSecondsBetweenDateAndNow(datefH: String?): Int {
        if (datefH.isNullOrEmpty()) return 0
        return try {
            val dateInstant = try {
                Instant.parse(datefH)
            } catch (e: Exception) {
                sanitizeAndParseDate(datefH) ?: return 0
            }
            val currentInstant = Clock.System.now()
            ((currentInstant.toEpochMilliseconds() - dateInstant.toEpochMilliseconds()) / 1000).toInt()
        } catch (e: Exception) {
            0
        }
    }

    fun diffSecondsBetweenDates(date1: String?, date2: String?): Int {
        if (date1.isNullOrEmpty() || date2.isNullOrEmpty()) return 0
        return try {
            val date1Instant = try {
                Instant.parse(date1)
            } catch (e: Exception) {
                sanitizeAndParseDate(date1) ?: return 0
            }
            val date2Instant = try {
                Instant.parse(date2)
            } catch (e: Exception) {
                sanitizeAndParseDate(date2) ?: return 0
            }
            ((date1Instant.toEpochMilliseconds() - date2Instant.toEpochMilliseconds()) / 1000).toInt()
        } catch (e: Exception) {
            0
        }
    }


    /**
     * Devuelve la fecha dada en el formato indicado, zona horaria local
     * Si el formato no está en QuipConstants.DateFormat, lanzará una excepción
     * */
    fun dateToSpecificFormat(date: String?, format: String): String {
        if (date.isNullOrEmpty()) return ""
        return try {
            val dateMoment = try {
                Instant.parse(date)
            } catch (e: Exception) {
                sanitizeAndParseDate(date) ?: return ""
            }
            val localTimeZone = TimeZone.currentSystemDefault()
            dateMoment.toLocalDateTime(localTimeZone).format(
                getLocalDateTimeFormat(format),
            )
        } catch (e: Exception) {
            ""
        }
    }

    /**
     * Devuelve la fecha dada en formato UTC (yyyy-MM-dd'T'HH:mm:ss'Z'), zona horaria UTC +00
     * */
    fun formatUTC(date: String?): String {
        if (date.isNullOrEmpty()) return ""
        return try {
            val dateInstant = try {
                Instant.parse(date)
            } catch (e: Exception) {
                sanitizeAndParseDate(date) ?: return ""
            }
            dateInstant.format(
                format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
                offset = TimeZone.UTC.offset
            )
        } catch (e: Exception) {
            ""
        }
    }

    fun formatMinutesToHourAndMinutes(min: Float): String {
        val hours = min / 60
        val minutes = min % 60
        return "${hours.toInt().toString().padStart(2, '0')}:${minutes.toInt().toString().padStart(2, '0')}"
    }

    fun formatMinutesToHourAndMinutes(min: Int): String {
        val hours = min / 60
        val minutes = min % 60
        return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}"
    }

    fun getTimeZone(): String {
        return TimeZone.currentSystemDefault().id
    }

    fun isDateInSunday(dateStr: String): Boolean {
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return false
            }
            val localDateTime = dateInstant.toLocalDateTime(TimeZone.UTC)
            localDateTime.dayOfWeek == kotlinx.datetime.DayOfWeek.SUNDAY
        } catch (e: Exception) {
            false
        }
    }
    /**
     * Devuelve true si la fecha está en el turno de noche (de 00:00 a 07:59 o de 20:00 a 23:59)
     * */
    fun isDateNightShift(dateStr: String): Boolean {
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return false
            }
            val localDateTime = dateInstant.toLocalDateTime(TimeZone.UTC)
            val hour = localDateTime.hour
            hour in 0..7 || hour >= 20
        } catch (e: Exception) {
            false
        }
    }

    fun getDateFromMilis(milis: String): String {
        return try {
            val millisLong = milis.toLongOrNull() ?: throw IllegalArgumentException("Invalid milliseconds: $milis")
            Instant.fromEpochMilliseconds(millisLong).format(
                format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
                offset = TimeZone.UTC.offset
            )
        } catch (e: Exception) {
            println("Error al convertir milisegundos a fecha: ${e.message}")
            throw e
        }
    }
    /**
     * Devuelve el tiempo una vez sumamos a la fecha los minutos indicados en formato $hourStr:$minuteStr:$secondsStr
     * */
    fun getTimeFromDatePlusMinutes(fromDate: String?, minutesToAdd: Int?): String {
        if (fromDate.isNullOrEmpty() || minutesToAdd == null) {
            return ""
        }
        return try {
            val dateInstant = try {
                Instant.parse(fromDate)
            } catch (e: Exception) {
                sanitizeAndParseDate(fromDate) ?: return ""
            }
            val newDateInstant = dateInstant.plus(
                minutesToAdd,
                DateTimeUnit.MINUTE
            )
            newDateInstant.format(
                format = getInstantFormat(QuipConstants.DateFormat.timePattern),
                offset = TimeZone.UTC.offset
            )
        } catch (e: Exception) {
            ""
        }
    }
    /**
     * Toma como parámetro un tiempo en segundos y lo devuelve en formato mm:ss
     * En caso de ser menos de un minuto, devuelve solo los segundos
     * */
    fun formatSecondsToMinutesAndSeconds(time: Int): String {
        val minutes = time / 60
        val seconds = time % 60
        return if (minutes > 0) {
            "${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"
        } else {
            seconds.toString()
        }
    }

    fun getSystemLocalCurrentDateTime(): String {
        val currentMoment = Clock.System.now()
        val localTimeZone = TimeZone.currentSystemDefault()
        return currentMoment.toLocalDateTime(localTimeZone).format(
            getLocalDateTimeFormat(QuipConstants.DateFormat.utcPattern1),
        )
    }

    fun getDateInLocalTime(dateStr: String?): String {
        if (dateStr.isNullOrEmpty()) return ""
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return ""
            }
            dateInstant.toLocalDateTime(TimeZone.currentSystemDefault()).format(
                getLocalDateTimeFormat(QuipConstants.DateFormat.utcPattern1),
            )
        } catch (e: Exception) {
            ""
        }
    }

    fun getDateInLocalTime(dateStr: String?, format: String): String {
        if (dateStr.isNullOrEmpty()) return ""
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return ""
            }
            dateInstant.toLocalDateTime(TimeZone.currentSystemDefault()).format(
                getLocalDateTimeFormat(format),
            )
        } catch (e: Exception) {
            ""
        }
    }

    /**
     * Devuelve los segundos pasados en minutos (sin añadir ceros delante). Si el tiempo es menor de 60 segundos, devuelve 1 minuto.
     * */
    fun formatSecondsToMinutes(time: Int): String {

        val minutes = (time + 59) / 60 // Redondea hacia arriba
        return minutes.toString()

    }
    /**
     * Devuelve las horas, minutos y segundos de la fecha indicada en formato HH:mm:ss
     * */
    fun getFormattedTimeFromDate(date: String?): String {
        if (date.isNullOrEmpty()) return ""
        return try {
            val dateInstant = try {
                Instant.parse(date)
            } catch (e: Exception) {
                sanitizeAndParseDate(date) ?: return ""
            }
            dateInstant.toLocalDateTime(TimeZone.UTC).format(
                getLocalDateTimeFormat(QuipConstants.DateFormat.timePattern),
            )
        } catch (e: Exception) {
            ""
        }
    }
    /**
     * Devuelve la fecha que se pasa como String en milisegundos desde 1970
     * */
    fun getMillisFromDatetime(dateStr: String?): Long {
        if (dateStr.isNullOrEmpty()) {
            return 0L
        }
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return 0L
            }
            dateInstant.toEpochMilliseconds()
        } catch (e: Exception) {
            0L
        }
    }

    fun isToday(dateStr: String?): Boolean {
        if (dateStr.isNullOrEmpty()) return false
        return try {
            val dateInstant = try {
                Instant.parse(dateStr)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateStr) ?: return false
            }
            val localDateTime = dateInstant.toLocalDateTime(TimeZone.UTC)
            val today = Clock.System.now().toLocalDateTime(TimeZone.UTC)
            localDateTime.year == today.year && localDateTime.month == today.month && localDateTime.day == today.day
        } catch (e: Exception) {
            false
        }
    }


    /**
     * Devuelve la fecha en milisegundos formateada a español (dd/MM/yyyy) sin hora
     * */
    fun getMillisecondsDateFormattedToSpanish(millis: Long?): String? {
        if (millis == null) return null
        return try {
            Instant.fromEpochMilliseconds(millis).format(
                format = getInstantFormat(QuipConstants.DateFormat.noUtcPatternSpanishWithoutTime),
                offset = TimeZone.UTC.offset
            )
        } catch (e: Exception) {
            null
        }
    }

    fun currentDateToSpecificFormat(format: String): String {
        return this.dateToSpecificFormat(
            date = Clock.System.now().format(
                format = getInstantFormat(QuipConstants.DateFormat.utcPattern1),
                offset = TimeZone.UTC.offset
            ),
            format = format
        )
    }

    /**
     * Verifica si una fecha en formato String está fuera del rango de días en el pasado
     *
     */
    fun isDateOutOfRange(dateString: String, daysInPast: Int, format: String): Boolean {
        return try {
            val dateInstant = try {
                Instant.parse(dateString)
            } catch (e: Exception) {
                sanitizeAndParseDate(dateString) ?: return false
            }
            val currentInstant = Clock.System.now()
            val pastLimit = currentInstant.minus(
                daysInPast.toDuration(DurationUnit.DAYS)
            )
            dateInstant < pastLimit
        } catch (e: Exception) {
            false
        }
    }

    fun isAfterDateInMillis(date1Str: String, millis: Long): Boolean {
        return try {
            val date1Instant = try {
                Instant.parse(date1Str)
            } catch (e: Exception) {
                sanitizeAndParseDate(date1Str) ?: return false
            }
            val millisInstant = Instant.fromEpochMilliseconds(millis)
            date1Instant > millisInstant
        } catch (e: Exception) {
            false
        }
    }


    /**
     * Intenta parsear un string de fecha probando varios formatos de LocalDateTime.
     * Primero intenta con los formatos UTC y luego con los formatos de hora local.
     * @param dateStr El string de fecha a parsear.
     * @return Un [Instant] si el parseo es exitoso, o null si ningún formato coincide.
     */
    private fun sanitizeAndParseDate(dateStr: String?): Instant? {
        if (dateStr.isNullOrEmpty()) return null

        // Lista de todos los formatos a probar
        val formatsToTry = QuipConstants.DateFormat.allFormats

        // Iterar sobre los formatos y intentar parsear
        for (format in formatsToTry) {
            try {
                // Intentamos parsear como LocalDateTime y convertir a Instant en UTC
                val localDateTime = getLocalDateTimeFormat(format).parse(dateStr)
                return localDateTime.toInstant(TimeZone.UTC)
            } catch (e: Exception) {
                // Ignoramos la excepción y probamos el siguiente formato
            }
        }
        // Si ningún formato funcionó
        return null
    }
}
